import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Popover as BasePopover } from '@base-ui/react/popover';
import { isEmpty } from 'lodash';

import { writePopupHeight } from '~/components/ui/internal/popupGrow';
import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

import { TEXTS } from './texts';

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type PopoverAlign = 'start' | 'center' | 'end';
/** A pixel offset, or a function of the anchor's and the popover's sizes for the chosen side. */
export type PopoverOffset = NonNullable<BasePopover.Positioner.Props['sideOffset']>;

/**
 * Where the popover opens, which also decides how much of the page it blocks. On every layer Tab
 * stays inside the popup and the rest of the page is hidden from assistive technology.
 * - `'modal'`: the layer MUI's dialogs and menus share, above the app's header, icon column and claim
 *   bar. It blocks the page: a press outside lands on a backdrop instead of the control under it,
 *   and the page does not scroll.
 * - `'page'`: below those bars, so the popover scrolls under them with its anchor. It leaves the page
 *   usable: a press outside reaches the control under it, and the page scrolls. Only for a popover
 *   anchored in the page content: one opened from a MUI dialog would open behind the dialog, and one
 *   opened from the header or the claim bar behind the bar.
 * - `'inline'`: inside the anchor element itself, so the browser scrolls it with the page instead of
 *   Base UI repositioning it after each scroll. It leaves the page usable, as the page layer does. The
 *   caller passes an anchor that is not interactive (a wrapper, not the button that opens it), that is
 *   positioned (any `position` except `static`, usually `relative`), so the popover is placed inside the
 *   scrolling area rather than relative to something outside it, that clips nothing (`overflow:
 *   visible` up to the scrolling area), and that has no click handler of its own, since a click in the
 *   popover bubbles to it. The popover is clipped by the scrolling area's edges and cannot rise above a
 *   stacking context an ancestor creates.
 */
export type PopoverLayer = 'modal' | 'page' | 'inline';

/**
 * Why the popover asks to close: the Escape key, a press outside it, or the visually hidden close
 * button that screen readers reach. The popover is controlled, so it stays open until `open` turns
 * false; a caller that ignores a reason keeps it open.
 */
export type PopoverCloseReason = 'escape-key' | 'outside-press' | 'close-press';

interface PopoverBaseProps {
  /** Shows the popover when `true` and an anchor element is set. */
  open: boolean;
  /** The element the popover is positioned against, usually the clicked control (`event.currentTarget`). */
  anchorEl: Element | null | undefined;
  onClose?: (reason: PopoverCloseReason) => void;
  /** Fired once the opening transition ends. */
  onOpenComplete?: () => void;
  /**
   * The side of the anchor the popover sits on. It flips to the opposite side when it does not fit.
   * @default 'bottom'
   */
  side?: PopoverSide;
  /**
   * Where the popover lines up along that side: its start edge with the anchor's start edge, the
   * two centers, or the end edges.
   * @default 'center'
   */
  align?: PopoverAlign;
  /** Distance from the anchor along the side, in pixels. A negative value moves it over the anchor. */
  sideOffset?: PopoverOffset;
  /** Nudge along the alignment axis, in pixels. */
  alignOffset?: PopoverOffset;
  children?: React.ReactNode;
  /** Applied to the popup surface. */
  className?: string;
  /** The popup's id, which the trigger's `aria-controls` names. `usePopoverState` supplies both. */
  id?: string;
  /**
   * Where the popover opens, and with it how much of the page it blocks (see `PopoverLayer`).
   * @default 'modal'
   */
  layer?: PopoverLayer;
}

interface LabelledPopoverProps extends PopoverBaseProps {
  /** The dialog's accessible name. */
  'aria-label': string;
  'aria-labelledby'?: never;
}

interface LabelledByPopoverProps extends PopoverBaseProps {
  'aria-label'?: never;
  /** The id of a visible heading inside the popover that names the dialog. */
  'aria-labelledby': string;
}

/** The popup has the `dialog` role, so it needs an accessible name: `aria-label` or `aria-labelledby`. */
export type PopoverProps = LabelledPopoverProps | LabelledByPopoverProps;

// The space MUI's popover kept between itself and the viewport edges. The same 16px bounds how
// far the popover slides along an edge before it flips, and caps its size below.
const VIEWPORT_MARGIN = 16;

// Once open, the popover keeps the side and alignment it opened with (`openedPlacement` below). It
// can still slide along the edge to stay in view. With `side: 'none'` Base UI skips its flip step,
// which is also what flips the alignment, so the alignment has to be kept too.
const LOCKED_COLLISION_AVOIDANCE = { side: 'none', align: 'shift', fallbackAxisSide: 'none' } as const;

// Base UI writes the placement it chose to the popup's data-side and data-align attributes.
const readPlacement = (popup: HTMLElement | null): { side: PopoverSide; align: PopoverAlign } | null => {
  const placedSide = popup?.getAttribute('data-side') as PopoverSide | null | undefined;
  const placedAlign = popup?.getAttribute('data-align') as PopoverAlign | null | undefined;
  return placedSide && placedAlign ? { side: placedSide, align: placedAlign } : null;
};

// The modal layer, where MUI's dialogs and menus sit too. Select menus, date pickers and dialogs
// opened from inside a popover mount later on the same layer, so they stack on top. The page layer
// sits below the app's fixed bars, and those menus and pickers still stack above it. Inside either
// stacking context the positioner follows the backdrop in document order, so it paints above it
// without a z-index of its own.
// An inline portal is inside the anchor, so it takes no box of its own (`contents`), and the
// positioner stacks above the page around it from the numeric scale instead.
const PORTAL_CLASSES: Record<PopoverLayer, string> = {
  modal: 'relative z-modal',
  page: 'relative z-page',
  inline: 'contents',
};

// An inline popover lives inside its anchor, so the anchor must not be a control: a dialog inside a
// button is invalid, becomes part of the button's name, and its clicks reach the button's handler.
const INTERACTIVE_ANCHOR_SELECTOR = 'button, a[href], input, select, textarea, [role="button"]';

// The surface: white, the paper radius, MUI elevation 8, and a size capped to the viewport minus
// the margin on both sides, scrolling inside. The grow-and-fade transition and its timing live in
// the `popover-popup` rules in index.css.
const popupClasses =
  'popover-popup overflow-y-auto overflow-x-hidden rounded-md bg-white shadow-elevation-8 outline-none ' +
  'max-h-[calc(100vh-32px)] max-w-[calc(100vw-32px)] min-h-16 min-w-16';

// Focus returns to the anchor rather than to the element that had focus before the popover opened:
// a trigger that never took focus (an SVG icon, or any button in Safari, which does not focus a
// clicked button) leaves nothing there, and focus would fall to the page.
const isFocusable = (element: Element | null | undefined): element is HTMLElement =>
  element instanceof HTMLElement && element.isConnected && element.tabIndex >= 0 && !element.matches(':disabled');

// Focus that moved to another element before the popover closed was moved on purpose: by the page,
// to the field a picked value went into, or by the user, pressing a control above the backdrop
// such as a snackbar's action.
const hasFocusMovedOutside = (popup: HTMLElement | null) => {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body && !popup?.contains(active);
};

const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (
    {
      open,
      anchorEl,
      onClose,
      onOpenComplete,
      side = 'bottom',
      align = 'center',
      sideOffset = 0,
      alignOffset = 0,
      children,
      className,
      id,
      layer = 'modal',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Popover: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the PopoverProps interface and to the component's signature.`,
        true
      );
    }
    // The union requires a name for typed callers. This covers untyped (JS) callers: a dialog
    // without a name is announced as just "dialog", a defect nobody sees.
    if (!ariaLabel && !ariaLabelledBy) {
      reportErrorInProductionOrThrow(
        'Popover has neither aria-label nor aria-labelledby. The popover is a dialog, so it needs an accessible name.',
        true
      );
    }

    const popupRef = useRef<HTMLDivElement | null>(null);
    const setPopupRef = useCallback(
      (element: HTMLDivElement | null) => {
        popupRef.current = element;
        // On mount, before the opening transition starts. offsetHeight ignores the starting scale.
        if (element) {
          writePopupHeight(element);
        }
        if (typeof ref === 'function') {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      [ref]
    );

    // Callers clear anchorEl in the same update that closes the popover. The last anchor keeps the
    // popover in place while its closing transition runs, and is where focus returns.
    const lastAnchorRef = useRef(anchorEl);
    useLayoutEffect(() => {
      if (anchorEl) {
        lastAnchorRef.current = anchorEl;
      }
    }, [anchorEl]);
    const positionAnchor = anchorEl ?? lastAnchorRef.current;

    if (layer === 'inline' && positionAnchor?.matches(INTERACTIVE_ANCHOR_SELECTOR)) {
      reportErrorInProductionOrThrow(
        'An inline Popover mounts inside its anchor, so the anchor cannot be a control. Pass a wrapper element as anchorEl.',
        true
      );
    }
    // Inline, the popover mounts inside the anchor. Otherwise straight into the body (see the portal below).
    const portalContainer =
      layer === 'inline' && positionAnchor instanceof HTMLElement ? positionAnchor : document.body;

    // Again as the close starts, for content that grew or shrank while the popover was open.
    useLayoutEffect(() => {
      if (!open && popupRef.current) {
        writePopupHeight(popupRef.current);
      }
    }, [open]);

    // The placement the popover opened with, kept through its closing transition and cleared when it
    // opens again. Base UI places it again whenever the content changes size, so a list filtered by
    // a search field would jump from one side of the anchor to the other while the user types.
    const [openedPlacement, setOpenedPlacement] = useState<ReturnType<typeof readPlacement>>(null);
    useLayoutEffect(() => {
      if (open) {
        setOpenedPlacement(null);
      }
    }, [open]);

    // Base UI runs the opening transition only when `open` changes. A popover mounted already open,
    // as callers do that render it only while it has an anchor, starts closed for one render so it
    // still grows in, as MUI's popover did.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => setHasMounted(true), []);

    const isOpen = hasMounted && open && Boolean(anchorEl);

    // A static anchor is not the positioner's containing block, so the popover would be placed
    // against an ancestor outside the scrolling area and trail its anchor while the area scrolls.
    useLayoutEffect(() => {
      if (isOpen && layer === 'inline' && anchorEl && getComputedStyle(anchorEl).position === 'static') {
        reportErrorInProductionOrThrow(
          'An inline Popover mounts inside its anchor, so the anchor must be positioned (any position except static).',
          true
        );
      }
    }, [isOpen, layer, anchorEl]);

    // Focus returns in the commit that closes the popover. Base UI would return it only once the
    // closing transition ends, and by then the user may have opened another popover, which would
    // lose its focus. The same commit makes the popup inert, which drops its focus to the page.
    // When focus is still in the popover or on the page, it goes to the anchor, or to the element
    // that had focus before the popover opened when the anchor cannot take it. Escape, a press on
    // the backdrop and the close button all close it that way. Focus that moved to another element
    // stays there. Recorded on opening: Base UI moves focus into the popup only a frame later.
    const focusedBeforeOpenRef = useRef<Element | null>(null);
    const wasOpenRef = useRef(false);
    useLayoutEffect(() => {
      if (isOpen && !wasOpenRef.current) {
        focusedBeforeOpenRef.current = document.activeElement;
      }
      if (!isOpen && wasOpenRef.current) {
        if (!hasFocusMovedOutside(popupRef.current)) {
          const anchor = lastAnchorRef.current;
          const target = isFocusable(anchor) ? anchor : focusedBeforeOpenRef.current;
          if (isFocusable(target)) {
            target.focus({ preventScroll: true });
          }
        }
      }
      wasOpenRef.current = isOpen;
    }, [isOpen]);

    const handleOpenChange = (nextOpen: boolean, details: BasePopover.Root.ChangeEventDetails) => {
      // Only these three reasons reach here. Base UI's others need parts this popover never renders
      // (a Trigger, an actions ref), and a popup that traps focus does not close when focus leaves
      // it, so a select menu opened from inside, which mounts outside the popup, keeps the popover open.
      if (!nextOpen) {
        onClose?.(details.reason as PopoverCloseReason);
      }
    };

    const handleOpenChangeComplete = (isOpen: boolean) => {
      if (isOpen) {
        setOpenedPlacement(readPlacement(popupRef.current));
        onOpenComplete?.();
      }
    };

    return (
      <BasePopover.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        onOpenChangeComplete={handleOpenChangeComplete}
        // The modal layer blocks the page: a backdrop takes a press outside, the page cannot scroll,
        // and Tab stays in the popup. The page and inline layers only keep Tab in the popup. They
        // cannot block: their popover sits among the page's own content, so a backdrop there would
        // cover some of the page and not the rest.
        // Base UI's third mode, which lets Tab leave the popup, is left out. It relies on Base UI's
        // own Trigger part to know where focus goes after the popup. This popover renders no Trigger,
        // so Tab past the last control would stop on an invisible placeholder, and focus would seem
        // to vanish.
        modal={layer === 'modal' ? true : 'trap-focus'}
      >
        {/* Straight into the body, mounted when it opens. Base UI would otherwise mount a popover
            opened from inside another one into that one's container, which can sit before a MUI
            dialog opened in between. Every layer shares z-modal, so the popover would paint
            behind that dialog. The container is a stacking context on the popover's layer (z-modal by
            default) because Base UI's backdrop, which blocks the page while the popover is open, comes
            without a z-index: without one it would paint under a MUI dialog, and a press outside a
            popover opened inside a dialog would reach the dialog's controls. */}
        <BasePopover.Portal container={portalContainer} className={PORTAL_CLASSES[layer]}>
          <BasePopover.Positioner
            className={layer === 'inline' ? 'z-10' : undefined}
            anchor={positionAnchor}
            side={openedPlacement?.side ?? side}
            align={openedPlacement?.align ?? align}
            collisionAvoidance={openedPlacement ? LOCKED_COLLISION_AVOIDANCE : undefined}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            // The margin keeps a popover off the window's edges. An inline popover is bounded by its
            // scrolling area instead, whose edge may be its anchor's edge, so it takes none.
            collisionPadding={layer === 'inline' ? 0 : VIEWPORT_MARGIN}
          >
            <BasePopover.Popup
              data-fs-component="popover"
              ref={setPopupRef}
              id={id}
              aria-label={ariaLabel}
              aria-labelledby={ariaLabelledBy}
              // Focus the popup surface on open, as MUI's popover did, so a screen reader announces the
              // popover as a whole before any control in it. A field that focuses itself on mount, such as
              // a search input with autoFocus, keeps the focus: Base UI moves focus only when it is not
              // already inside the popup.
              initialFocus={popupRef}
              // The popover returns focus itself, as it closes (see `focusedBeforeOpenRef`).
              finalFocus={false}
              className={cn(popupClasses, className)}
            >
              {children}
              {/* Base UI enables a modal popup's focus trap, and hides the rest of the page, only
                  when the popup contains a Close part (the `modal` prop's docs on Popover.Root). It
                  is visually hidden, as those docs suggest, and kept out of the Tab order, because
                  keyboard users close with Escape. It stays reachable for screen readers, which on
                  a touch device have no Escape key. */}
              <BasePopover.Close className="sr-only" tabIndex={-1}>
                {TEXTS.CLOSE_BUTTON}
              </BasePopover.Close>
            </BasePopover.Popup>
          </BasePopover.Positioner>
        </BasePopover.Portal>
      </BasePopover.Root>
    );
  }
);

Popover.displayName = 'Popover';

export default Popover;
